import { useCallback, useState } from 'react';

import PropTypes from 'prop-types';

import { useAppDispatch, useAppSelector } from '@core/store/hooks';
import { PdfViewer } from '@features/dataroom';

import { setCanvasPage, queueChatQuote, selectCanvasTargetPage } from '../store/workspaceSlice';

import { SelectionBubble } from './SelectionBubble';

/**
 * Canvas PDF viewer wired to page-aware canvas state.
 *
 * The rendered PDF is the safe, page-addressable preview for every report
 * (including decks — one slide is one page). This wrapper tracks the visible
 * page into Redux (so chat can attach it as context) and exposes a text-selection
 * bubble that quotes the selection, with its page number, into the composer.
 */
export function CanvasPdfViewer({ url }) {
  const dispatch = useAppDispatch();
  const targetPage = useAppSelector(selectCanvasTargetPage);
  const [selection, setSelection] = useState(null);

  const handlePageChange = useCallback(
    (page, numPages) => {
      dispatch(setCanvasPage({ page, numPages }));
    },
    [dispatch]
  );

  const handleVerb = useCallback(
    (intent) => {
      if (!selection) return;
      dispatch(queueChatQuote({ text: selection.text, page: selection.page, intent }));
      window.getSelection()?.removeAllRanges();
      setSelection(null);
    },
    [selection, dispatch]
  );

  return (
    <div className="flex h-full flex-col">
      <div className="min-h-0 flex-1">
        <PdfViewer
          key={url}
          url={url}
          initialPage={targetPage ?? undefined}
          onPageChange={handlePageChange}
          onTextSelected={setSelection}
        />
      </div>
      <SelectionBubble selection={selection} onVerb={handleVerb} />
    </div>
  );
}

CanvasPdfViewer.propTypes = {
  url: PropTypes.string.isRequired,
};
